import { useCallback, useEffect, useRef, useState } from "react";
import { Box, Input, Text } from "@salestrak/ui";
import type { Omittable } from "@salestrak/ui";

/** Debounce helper — returns a stable debounced function with cancel support. */
function useDebouncedCallback<T extends (...args: any[]) => any>(
  callback: T,
  delay: number,
) {
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const cancel = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  const debounced = useCallback(
    (...args: Parameters<T>) => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => callbackRef.current(...args), delay);
    },
    [delay],
  ) as T;

  return { debounced, cancel };
}

// Input is always w-full, so the width token constrains the wrapper instead.
const WIDTH_PX = {
  xs: 64,
  sm: 96,
  md: 128,
  lg: 160,
  xl: 192,
  full: "100%",
} as const;

export function EditableField({
  fieldId,
  label,
  initialValue,
  disabled,
  onSave,
  placeholder,
  inputType = "text",
  width = "full",
  validate,
  errorMessage,
}: Omittable<{
  fieldId: string;
  /** Accessible name for the input. The surrounding `SettingsGroupRow` label
   *  is visual-only (no `htmlFor` association), so without this the field is
   *  anonymous to a screen reader. */
  label: string;
  initialValue: string;
  disabled?: boolean;
  onSave: (fieldId: string, value: string) => void;
  placeholder?: string;
  inputType?: "text" | "email" | "tel";
  width?: keyof typeof WIDTH_PX;
  validate?: (value: string) => boolean;
  errorMessage?: string;
}>) {
  const [value, setValue] = useState(initialValue);
  const [invalid, setInvalid] = useState(false);
  const { debounced: debouncedSave, cancel: cancelSave } = useDebouncedCallback(onSave, 300);

  useEffect(() => {
    setValue(initialValue);
    setInvalid(false);
  }, [initialValue]);

  const handleChange = useCallback(
    (text: string) => {
      setValue(text);
      setInvalid(false);
      if (!disabled) {
        const isValid = !validate || validate(text);
        if (isValid) {
          debouncedSave(fieldId, text);
        } else {
          cancelSave();
        }
      }
    },
    [fieldId, disabled, debouncedSave, cancelSave, validate],
  );

  const handleBlur = useCallback(() => {
    if (validate && !validate(value)) {
      setInvalid(true);
    }
  }, [validate, value]);

  return (
    <Box direction="column" gap="xs" width={WIDTH_PX[width]}>
      <Input
        aria-label={label}
        aria-invalid={invalid || undefined}
        disabled={disabled}
        onBlur={handleBlur}
        onChange={(e) => handleChange(e.target.value)}
        placeholder={placeholder}
        type={inputType}
        value={value}
      />
      {invalid && errorMessage ? (
        <Text size="sm" variant="destructive">{errorMessage}</Text>
      ) : null}
    </Box>
  );
}
